import type { User, Group } from '@microsoft/microsoft-graph-types-beta';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';

import type {
  GroupCreateOperation,
  GroupDetails,
  GroupMemberOperation,
  GroupUpdateOperation,
  RBACGroup,
  RBACUser,
  RoleMember,
} from '../../../types';
import { msalInstance, acquireAccessToken } from '../../authentication/services/msal';

// Graph API response type
interface GraphResponse<T> {
  '@odata.context': string;
  '@odata.nextLink'?: string;
  value: T[];
}

// Mirrors RTK Query's baseQuery shape loosely enough to accept it: the arg may be
// a URL string (most calls) or a FetchArgs object (when a call needs custom
// headers / responseHandler, e.g. the text/plain $count endpoint), and the result
// may be sync or a Promise.
type BQResult = { data?: unknown; error?: FetchBaseQueryError };
type FetchWithBQ = (arg: string | FetchArgs) => BQResult | PromiseLike<BQResult>;

// Graph throttles with 429 (and occasionally 503). Retry a bounded number of
// times, honoring Retry-After when present, else exponential backoff.
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 500;
// Cap the wait so an oversized (or hostile) Retry-After can't stall a query.
const MAX_RETRY_WAIT_MS = 30_000;

const retryAfterMs = (error: FetchBaseQueryError, attempt: number): number => {
  const meta = (error as { meta?: { response?: Response } }).meta;
  const header = meta?.response?.headers.get('retry-after');
  const seconds = header ? Number(header) : NaN;
  const ms = Number.isFinite(seconds) ? seconds * 1000 : RETRY_BASE_MS * 2 ** attempt;
  return Math.min(ms, MAX_RETRY_WAIT_MS);
};

const fetchWithRetry = async (
  fetchWithBQ: FetchWithBQ,
  arg: string | FetchArgs,
  { retryOn404 = false }: { retryOn404?: boolean } = {}
) => {
  for (let attempt = 0; ; attempt++) {
    const response = await fetchWithBQ(arg);
    const status = response.error?.status;
    const throttled = status === 429 || status === 503;
    // A just-created resource can briefly 404 until Graph replicates it. Callers that
    // know the resource should exist (read-your-write, e.g. loading a freshly-created
    // ACL by id) opt into retrying that window with the same backoff.
    const notReplicatedYet = retryOn404 && status === 404;
    if ((throttled || notReplicatedYet) && attempt < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, retryAfterMs(response.error!, attempt)));
      continue;
    }
    return response;
  }
};

// Run an async mapper over items with a cap on how many run at once, so a user
// in many groups doesn't fan out into a throttling burst (see getUserMembershipInheritance).
const MAX_CONCURRENT_REQUESTS = 5;
const mapWithConcurrency = async <T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  limit = MAX_CONCURRENT_REQUESTS
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

// Helper function to fetch all pages of a Graph API response. Throws the Graph
// error (rather than a cryptic "cannot read value of undefined") so callers can
// surface it; run the queryFn body through asQueryResult to turn it into an RTK
// Query error.
const getAllResults = async <T>(
  fetchWithBQ: FetchWithBQ,
  nextLink: string,
  options?: { retryOn404?: boolean }
): Promise<T[]> => {
  const results: T[] = [];
  let firstPage = true;
  while (nextLink) {
    // retryOn404 only makes sense for the first fetch (read-your-write: the resource may
    // not have replicated yet). A 404 on an @odata.nextLink is an expired skiptoken and
    // won't resolve by retrying, so don't burn backoff on continuation pages.
    const response = await fetchWithRetry(fetchWithBQ, nextLink, firstPage ? options : undefined);
    firstPage = false;
    if (response.error) throw response.error;
    const data = response.data as GraphResponse<T>;
    results.push(...data.value);
    nextLink = data['@odata.nextLink'] ?? '';
  }
  return results;
};

// Run a queryFn body and convert a thrown Graph error (see getAllResults) into an
// RTK Query error result, so each queryFn doesn't repeat the same try/catch.
const asQueryResult = async <T>(
  produce: () => Promise<T>
): Promise<{ data: T } | { error: FetchBaseQueryError }> => {
  try {
    return { data: await produce() };
  } catch (error) {
    return { error: error as FetchBaseQueryError };
  }
};

// The group that syncs cloud security groups back to on-prem AD. ACLs must be
// members of it to actually take effect on file shares — ProvisionProject nests
// every new ACL into it (see getAADWritebackGroup / getACLWritebackMembership).
const AAD_WRITEBACK_GROUP_NAME = 'AAD Group Writeback';

export const graphApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://graph.microsoft.com/beta',
    prepareHeaders: async (headers) => {
      const token = await acquireAccessToken(msalInstance);
      headers.set('Authorization', `Bearer ${token}`);
      headers.append('ConsistencyLevel', 'eventual');
      return headers;
    },
  }),

  endpoints: (builder) => ({
    addGroupMember: builder.mutation<void, GroupUpdateOperation>({
      // No invalidatesTags. The Users page stays fresh via the optimistic
      // getUserGroupMembership patch below; the Roles-page lists are patched
      // optimistically in SubmitRoleChanges and re-read on demand via the Refresh
      // button. Invalidating here would refetch Graph's eventually-consistent
      // memberOf/transitiveMemberOf index, which lags the write by seconds and
      // clobbers the optimistic patch with a stale (item-missing) read. See ADR-0002.
      async onQueryStarted(operation, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            graphApi.util.updateQueryData('getUserGroupMembership', operation.memberId, (data) => {
              return [
                ...data,
                {
                  displayName: operation.groupDisplayName,
                  id: operation.groupId,
                  transitive: false,
                },
              ];
            })
          );
        } catch {}
      },
      query: (operation) => ({
        body: {
          '@odata.id': `https://graph.microsoft.com/beta/directoryObjects/${operation.memberId}`,
        },
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        url: `/groups/${operation.groupId}/members/$ref`,
      }),
    }),

    // Owners use a separate Graph relationship from members, so they need their own
    // mutations; the operation shape is identical (memberId = the owner user).
    addGroupOwner: builder.mutation<void, GroupUpdateOperation>({
      // No invalidatesTags — owners are patched optimistically in SubmitRoleChanges
      // and re-read on demand via Refresh, same as members (ADR-0002).
      query: (operation) => ({
        body: {
          '@odata.id': `https://graph.microsoft.com/beta/directoryObjects/${operation.memberId}`,
        },
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        url: `/groups/${operation.groupId}/owners/$ref`,
      }),
    }),

    // Add a group as a member of another group
    addGroupToGroup: builder.mutation<void, GroupMemberOperation>({
      // No invalidatesTags — see addGroupMember / removeGroupMember (ADR-0002). The ACL
      // page's submit patches getACLGroupRoleMembers optimistically; invalidating here
      // would refetch Graph's eventually-consistent members index (which lags the write
      // by seconds) and clobber that patch with a stale, role-missing read — so a just-
      // added role (and the effective users derived from it) would vanish. ProvisionProject
      // navigates to a freshly-loaded ACL page after nesting, so it needs no invalidation.
      query: (operation) => ({
        body: {
          '@odata.id': `https://graph.microsoft.com/beta/directoryObjects/${operation.memberId}`,
        },
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        url: `/groups/${operation.groupId}/members/$ref`,
      }),
    }),

    // Create a new cloud security group
    createGroup: builder.mutation<RBACGroup, GroupCreateOperation>({
      // No invalidatesTags. Invalidating ROLE_LIST/ACL_LIST would refetch Graph's
      // eventually-consistent name index (getRoleGroups' startswith(Role_) filter /
      // getACLGroups' $search "ACL_"), which lags the write by seconds and returns a
      // list *without* the just-created group — caching stale data so the new group is
      // missing from the RoleSelect / ACL picker until a manual reload. Instead we append
      // the created group to the matching list cache optimistically once the POST
      // succeeds. Extends the ADR-0001 → 0002 reasoning (member/owner lists) to group
      // creation; see ADR-0004.
      async onQueryStarted(_operation, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          // Freshly-created cloud groups are never on-prem-synced; mirror each list
          // query's own mapper so the appended row is shaped like a fetched one.
          const entry: RBACGroup = {
            description: created.description,
            displayName: created.displayName,
            id: created.id,
            onPremisesSyncEnabled: undefined,
            transitive: false,
          };
          const appendIfAbsent = (draft: RBACGroup[]) => {
            if (!draft.some((group) => group.id === created.id)) draft.push(entry);
          };
          // Route by the fixed display-name prefix every creation form applies
          // (Role_ / ACL_); anything else patches nothing and reconciles on next read.
          // updateQueryData only patches a live cache entry, so this relies on the
          // caller having the matching list query subscribed (every create form does —
          // RoleForm/StandardACLForm/ProvisionProject); a no-op otherwise.
          if (created.displayName.startsWith('Role_')) {
            dispatch(graphApi.util.updateQueryData('getRoleGroups', undefined, appendIfAbsent));
          } else if (created.displayName.startsWith('ACL_')) {
            dispatch(graphApi.util.updateQueryData('getACLGroups', undefined, appendIfAbsent));
          }
        } catch {}
      },
      query: (operation) => ({
        body: {
          displayName: operation.displayName,
          groupTypes: [],
          mailEnabled: false,
          mailNickname: operation.mailNickname,
          securityEnabled: true,
          // Only send description when provided — Graph treats it as optional, and the
          // standard ACL form may leave it blank (don't POST an empty string).
          ...(operation.description ? { description: operation.description } : {}),
        },
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        url: '/groups',
      }),
      transformResponse: (response: Group): RBACGroup => ({
        description: response.description ?? undefined,
        displayName: response.displayName!,
        id: response.id!,
      }),
    }),

    // Get the AAD Group Writeback group by display name
    getAADWritebackGroup: builder.query<RBACGroup, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const groups: Group[] = await getAllResults<Group>(
            fetchWithBQ,
            `/groups?$filter=displayName eq '${AAD_WRITEBACK_GROUP_NAME}'&$select=id,displayName`
          );
          if (!groups.length) {
            // Thrown so asQueryResult funnels it into the same { error } result as a
            // network/parse failure — the not-found case stays a CUSTOM_ERROR to callers.
            throw {
              error: `${AAD_WRITEBACK_GROUP_NAME} group not found`,
              status: 'CUSTOM_ERROR',
            } satisfies FetchBaseQueryError;
          }
          return {
            displayName: groups[0].displayName!,
            id: groups[0].id!,
          };
        });
      },
    }),

    // Users assigned DIRECTLY to an ACL group (not via a nested Role group). The
    // model says ACLs should contain only Role_* groups, so any direct user here is
    // policy drift worth surfacing — getACLGroupRoleMembers filters these out of the
    // role list. The microsoft.graph.user cast narrows /members to just user objects.
    getACLGroupDirectUserMembers: builder.query<
      Pick<RBACUser, 'accountEnabled' | 'id' | 'displayName' | 'userPrincipalName'>[],
      string
    >({
      providesTags: (result, error, id) => [{ id, type: 'Group' }],
      async queryFn(aclGroupId, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const users: User[] = await getAllResults<User>(
            fetchWithBQ,
            `/groups/${aclGroupId}/members/microsoft.graph.user?$select=id,displayName,userPrincipalName,accountEnabled`,
            // Read-your-write: tolerate the replication 404 on a freshly-created ACL.
            { retryOn404: true }
          );
          return users
            .map((user) => ({
              accountEnabled: user.accountEnabled ?? true,
              displayName: user.displayName!,
              id: user.id!,
              userPrincipalName: user.userPrincipalName!,
            }))
            .filter((user) => user.displayName);
        });
      },
    }),

    // Get all Role groups that are direct members of an ACL group
    getACLGroupRoleMembers: builder.query<RBACGroup[], string>({
      providesTags: (result, error, id) => [{ id, type: 'Group' }],
      async queryFn(aclGroupId, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const groups: Group[] = await getAllResults<Group>(
            fetchWithBQ,
            `/groups/${aclGroupId}/members?$count=true&$search="displayName:Role_"&$select=id,displayName,description`,
            // Read-your-write: tolerate the replication 404 on a freshly-created ACL.
            { retryOn404: true }
          );
          return groups
            .map((group) => ({
              description: group.description ?? undefined,
              displayName: group.displayName!,
              id: group.id!,
              transitive: false,
            }))
            .filter((group) => group.displayName);
        });
      },
    }),

    // Get all ACL groups
    getACLGroups: builder.query<RBACGroup[], void>({
      providesTags: [{ id: 'ACL_LIST', type: 'Group' }],
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        // Fetch all pages of groups
        const groups: Group[] = await getAllResults<Group>(
          fetchWithBQ,
          '/groups?$count=true&$search="displayName:ACL_"&$select=id,displayName,onPremisesSyncEnabled'
        );
        // Convert Groups to RBACGroups (remove groups with missing displayName). The
        // ACL_ $search also matches on-prem-mastered ACLs (synced from on-prem AD); the
        // ACL page hides those from the picker behind a toggle (see isOnPremGroup).
        return {
          data: groups
            .map((group) => ({
              description: group.description ?? undefined,
              displayName: group.displayName!,
              id: group.id!,
              onPremisesSyncEnabled: group.onPremisesSyncEnabled ?? undefined,
              transitive: false,
            }))
            .filter((group) => group.displayName),
        };
      },
    }),

    // Whether an ACL is a member of the AAD Group Writeback group — i.e. whether it
    // actually syncs to on-prem AD. ProvisionProject nests new ACLs there directly,
    // so a missing membership means the ACL grants nothing in practice (policy drift
    // worth flagging). Checks direct parent groups by name.
    getACLWritebackMembership: builder.query<boolean, string>({
      providesTags: (result, error, id) => [{ id, type: 'Group' }],
      async queryFn(aclGroupId, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const parentGroups: Group[] = await getAllResults<Group>(
            fetchWithBQ,
            `/groups/${aclGroupId}/memberOf/microsoft.graph.group?$select=id,displayName`,
            // Read-your-write: tolerate the replication 404 on a freshly-created ACL.
            { retryOn404: true }
          );
          return parentGroups.some((group) => group.displayName === AAD_WRITEBACK_GROUP_NAME);
        });
      },
    }),

    // Fetch a single group by id. Used so the ACL page can show details for the
    // ACL in the URL even when it isn't in the getACLGroups picker list (e.g. an
    // on-prem group we filter out, or direct navigation / refresh).
    getGroupById: builder.query<RBACGroup, string>({
      providesTags: (result, error, id) => [{ id, type: 'Group' }],
      async queryFn(groupId, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const response = await fetchWithRetry(
            fetchWithBQ,
            `/groups/${groupId}?$select=id,displayName,description,onPremisesSyncEnabled`,
            // Loaded right after creation (ProvisionProject / StandardACLForm navigate
            // to the new ACL) — ride out the replication 404 instead of showing nothing.
            { retryOn404: true }
          );
          if (response.error) throw response.error;
          const group = response.data as Group;
          return {
            description: group.description ?? undefined,
            displayName: group.displayName!,
            id: group.id!,
            onPremisesSyncEnabled: group.onPremisesSyncEnabled ?? undefined,
            transitive: false,
          };
        });
      },
    }),

    // A single group's kind (group type + membership type) for the role header. Reads
    // one group object (not a collection), so it goes through fetchWithRetry directly.
    getGroupDetails: builder.query<GroupDetails, string>({
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const response = await fetchWithRetry(
            fetchWithBQ,
            `/groups/${id}?$select=id,groupTypes,securityEnabled,mailEnabled`
          );
          if (response.error) throw response.error;
          const group = response.data as Group;
          return {
            groupTypes: group.groupTypes ?? [],
            mailEnabled: group.mailEnabled ?? false,
            securityEnabled: group.securityEnabled ?? false,
          };
        });
      },
    }),

    // Members of a group (the role's assigned users). Normally users; a nested
    // group can rarely appear, flagged isGroup so the UI surfaces it (see RoleMember).
    getGroupMembers: builder.query<RoleMember[], string>({
      providesTags: (_result, _error, id) => [{ id, type: 'Member' }],
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const members: User[] = await getAllResults<User>(
            fetchWithBQ,
            `/groups/${id}/members?$count=true&$select=id,displayName,userPrincipalName,accountEnabled,jobTitle,officeLocation`,
            // A just-created group can 404 here until it replicates (read-your-write,
            // ADR-0003) — ride that out rather than surfacing a load error.
            { retryOn404: true }
          );
          return members
            .map((member) => ({
              accountEnabled: member.accountEnabled ?? true,
              displayName: member.displayName!,
              id: member.id!,
              // Users carry a UPN; a nested group member won't — use that to flag it.
              isGroup: !member.userPrincipalName,
              jobTitle: member.jobTitle ?? '',
              officeLocation: member.officeLocation ?? '',
              userPrincipalName: member.userPrincipalName ?? '',
            }))
            .filter((member) => member.displayName)
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
        });
      },
    }),

    // Owners of a group (who administers it). Graph only allows users as owners.
    getGroupOwners: builder.query<RBACUser[], string>({
      providesTags: (_result, _error, id) => [{ id, type: 'Owner' }],
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const owners: User[] = await getAllResults<User>(
            fetchWithBQ,
            `/groups/${id}/owners?$count=true&$select=id,displayName,userPrincipalName,accountEnabled,jobTitle,officeLocation`,
            // Read-your-write: tolerate the replication 404 on a freshly-created group.
            { retryOn404: true }
          );
          return owners
            .map((owner) => ({
              accountEnabled: owner.accountEnabled ?? true,
              displayName: owner.displayName!,
              id: owner.id!,
              jobTitle: owner.jobTitle ?? '',
              officeLocation: owner.officeLocation ?? '',
              userPrincipalName: owner.userPrincipalName ?? '',
            }))
            .filter((owner) => owner.displayName)
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
        });
      },
    }),

    // ACL groups a single Role group confers: the role's transitive ACL membership,
    // with each entry flagged transitive = "reached via ACL-in-ACL nesting, not a
    // direct membership". Direct ones are removable; transitive ones can only be
    // managed at their source (the Roles page badges them, like the Users page).
    // Tagged on the role id so a nesting change (memberId = role) refetches it.
    getRoleACLGroupMembership: builder.query<RBACGroup[], string>({
      providesTags: (_result, _error, id) => [{ id, type: 'Group' }],
      async queryFn(roleId, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          // Read-your-write: tolerate the replication 404 on a freshly-created role.
          const [transitiveGroups, directGroups]: [Group[], Group[]] = await Promise.all([
            getAllResults<Group>(
              fetchWithBQ,
              `/groups/${roleId}/transitiveMemberOf?$count=true&$search="displayName:ACL_"&$select=id,displayName,onPremisesSyncEnabled`,
              { retryOn404: true }
            ),
            getAllResults<Group>(
              fetchWithBQ,
              `/groups/${roleId}/memberOf?$count=true&$search="displayName:ACL_"&$select=id,displayName`,
              { retryOn404: true }
            ),
          ]);
          const directIds = new Set(directGroups.map((group) => group.id));
          return transitiveGroups
            .map((group) => ({
              displayName: group.displayName!,
              id: group.id!,
              onPremisesSyncEnabled: group.onPremisesSyncEnabled ?? undefined,
              transitive: !directIds.has(group.id),
            }))
            .filter((group) => group.displayName)
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
        });
      },
    }),

    // Get all ACL groups that a list of Role groups is a member of
    getRoleGroupACLGroupMembership: builder.query<RBACGroup[], string[]>({
      // TODO: There might be too much logic in here
      async queryFn(roleGroupIds, _queryApi, _extraOptions, fetchWithBQ) {
        const [...ACLGroups] = await Promise.all(
          roleGroupIds.map(async (roleGroupId) => {
            // Fetch all pages of group's group membership
            const aclGroupResults: Group[] = await getAllResults<Group>(
              fetchWithBQ,
              `/groups/${roleGroupId}/memberOf?$count=true&$search="displayName:ACL_"&$select=id,displayName`
            );
            return aclGroupResults.map((aclGroup) => ({
              displayName: aclGroup.displayName!,
              id: aclGroup.id!,
              parentGroupID: roleGroupId,
            }));
          })
        );
        return {
          data: ACLGroups.flat().sort((a, b) => a.displayName!.localeCompare(b.displayName!)),
        };
      },
    }),

    // Get all Role groups
    getRoleGroups: builder.query<RBACGroup[], void>({
      providesTags: [{ id: 'ROLE_LIST', type: 'Group' }],
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        // Fetch all pages of groups
        const groups: Group[] = await getAllResults<Group>(
          fetchWithBQ,
          "/groups?$count=true&$filter=startswith(displayName,'Role_')&$select=id,displayName,description"
        );
        // Convert Groups to RBACGroups (remove groups with missing displayName)
        return {
          data: groups
            .map((group) => ({
              description: group.description ?? undefined,
              displayName: group.displayName!,
              id: group.id!,
              transitive: false,
            }))
            .filter((group) => group.displayName),
        };
      },
    }),

    // For a set of Role groups, the (transitive) user members of each, keyed by role
    // id. Used on the ACL page to attribute each effective user to the specific role
    // granting access, and to preview the users a pending role-add would grant access.
    getRolesUserMembers: builder.query<
      Record<
        string,
        Pick<RBACUser, 'accountEnabled' | 'id' | 'displayName' | 'userPrincipalName'>[]
      >,
      string[]
    >({
      async queryFn(roleIds, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const result: Record<
            string,
            Pick<RBACUser, 'accountEnabled' | 'id' | 'displayName' | 'userPrincipalName'>[]
          > = {};
          await mapWithConcurrency(roleIds, async (roleId) => {
            const users: User[] = await getAllResults<User>(
              fetchWithBQ,
              `/groups/${roleId}/transitiveMembers/microsoft.graph.user?$select=id,displayName,userPrincipalName,accountEnabled`
            );
            result[roleId] = users
              .map((user) => ({
                accountEnabled: user.accountEnabled ?? true,
                displayName: user.displayName!,
                id: user.id!,
                userPrincipalName: user.userPrincipalName!,
              }))
              .filter((user) => user.displayName);
          });
          return result;
        });
      },
    }),

    // Get all Role groups that the user is a member of
    getUserGroupMembership: builder.query<RBACGroup[], string>({
      providesTags: (result, error, id) => [{ id: id, type: 'User' }],
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        // Fetch all pages of user's group membership
        const groups: Group[] = await getAllResults<Group>(
          fetchWithBQ,
          `/users/${id}/memberOf?$count=true&$search="displayName:Role_"&$select=id,displayName`
        );
        // Convert Groups to RBACGroups (remove groups with missing displayName)
        return {
          data: groups
            .map((group) => ({
              description: group.description ?? undefined,
              displayName: group.displayName!,
              id: group.id!,
              transitive: false,
            }))
            .filter((group) => group.displayName),
        };
      },
    }),

    // Resolve WHERE each transitively-inherited membership (role OR ACL) comes
    // from. /transitiveMemberOf returns a flat set with no path, so for every
    // group the user is a DIRECT member of, fetch the groups it (transitively)
    // belongs to and attribute those back to that direct group — the actionable
    // membership to change. Returns a map of groupId -> the direct group(s) that
    // confer it (covers both Role_ and ACL_, so it serves role and ACL provenance).
    getUserMembershipInheritance: builder.query<Record<string, RBACGroup[]>, string>({
      async queryFn(userId, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const directGroups: Group[] = await getAllResults<Group>(
            fetchWithBQ,
            `/users/${userId}/memberOf/microsoft.graph.group?$count=true&$select=id,displayName`
          );
          const inheritance: Record<string, RBACGroup[]> = {};
          // Bounded concurrency: 1 + N calls (N = direct group count) capped so a
          // user in many groups doesn't fan out into a throttling burst. (A Graph
          // $batch would cut the round-trips further — a worthwhile future optimization.)
          await mapWithConcurrency(directGroups, async (group) => {
            if (!group.id) return;
            const conferredGroups: Group[] = await getAllResults<Group>(
              fetchWithBQ,
              `/groups/${group.id}/transitiveMemberOf/microsoft.graph.group?$count=true&$select=id,displayName`
            );
            for (const conferred of conferredGroups) {
              if (!conferred.id) continue;
              (inheritance[conferred.id] ??= []).push({
                displayName: group.displayName!,
                id: group.id,
                transitive: false,
              });
            }
          });
          return inheritance;
        });
      },
    }),

    // ACL groups the user holds NOT via a role: either added straight to the ACL
    // group (direct) or to a nested group of it (transitive). Distinct from the
    // role-derived ACLs that getRoleGroupACLGroupMembership computes. We fetch the
    // transitive set and the direct set, then flag transitive = "in transitive but
    // not direct" so only genuinely-nested ACLs get the non-removable badge.
    getUserTransitiveACLMembership: builder.query<RBACGroup[], string>({
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          const [transitiveGroups, directGroups]: [Group[], Group[]] = await Promise.all([
            getAllResults<Group>(
              fetchWithBQ,
              `/users/${id}/transitiveMemberOf?$count=true&$search="displayName:ACL_"&$select=id,displayName`
            ),
            getAllResults<Group>(
              fetchWithBQ,
              `/users/${id}/memberOf?$count=true&$search="displayName:ACL_"&$select=id,displayName`
            ),
          ]);
          const directIds = new Set(directGroups.map((group) => group.id));
          return transitiveGroups
            .map((group) => ({
              description: group.description ?? undefined,
              displayName: group.displayName!,
              id: group.id!,
              transitive: !directIds.has(group.id),
            }))
            .filter((group) => group.displayName);
        });
      },
    }),

    // Get all Role groups that the user is a transitive member of
    getUserTransitiveGroupMembership: builder.query<RBACGroup[], string>({
      async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
        return asQueryResult(async () => {
          // Fetch all pages of user's group membership
          const groups: Group[] = await getAllResults<Group>(
            fetchWithBQ,
            `/users/${id}/transitiveMemberOf?$count=true&$search="displayName:Role_"&$select=id,displayName`
          );
          // Convert Groups to RBACGroups (remove groups with missing displayName)
          return groups
            .map((group) => ({
              description: group.description ?? undefined,
              displayName: group.displayName!,
              id: group.id!,
              transitive: true,
            }))
            .filter((group) => group.displayName);
        });
      },
    }),

    // Get all users
    getUsers: builder.query<RBACUser[], void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const users: User[] = await toast.promise(
          getAllResults(
            fetchWithBQ,
            "/users?$select=id,userPrincipalName,displayName,accountEnabled,jobTitle,officeLocation,country,department&$filter=(userType eq 'Member' and companyName ne null)&$count=true"
          ),
          {
            error: 'Failed to load users list',
            pending: 'Loading users list',
            success: 'User list loaded',
          },
          { closeOnClick: true }
        );
        // Convert Users to RBACUsers (remove users with missing displayName or userPrincipalName)
        return {
          data: users
            .map((user) => ({
              accountEnabled: user.accountEnabled!,
              country: user.country ?? '',
              department: user.department ?? '',
              displayName: user.displayName!,
              id: user.id!,
              jobTitle: user.jobTitle!,
              officeLocation: user.officeLocation!,
              userPrincipalName: user.userPrincipalName!,
            }))
            .filter((user) => user.displayName && user.userPrincipalName),
        };
      },
    }),

    removeGroupMember: builder.mutation<void, GroupUpdateOperation>({
      // No invalidatesTags — see addGroupMember (ADR-0002). The Users-page optimistic
      // patch below stays; the Roles-page lists are patched in SubmitRoleChanges.
      async onQueryStarted(operation, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            graphApi.util.updateQueryData('getUserGroupMembership', operation.memberId, (data) => {
              return data.filter((group) => group.id !== operation.groupId);
            })
          );
        } catch {}
      },
      query: (operation) => ({
        method: 'DELETE',
        url: `/groups/${operation.groupId}/members/${operation.memberId}/$ref`,
      }),
    }),

    removeGroupOwner: builder.mutation<void, GroupUpdateOperation>({
      // No invalidatesTags — see addGroupOwner (ADR-0002).
      query: (operation) => ({
        method: 'DELETE',
        url: `/groups/${operation.groupId}/owners/${operation.memberId}/$ref`,
      }),
    }),
  }),

  reducerPath: 'graphApi',

  tagTypes: ['Group', 'User', 'Member', 'Owner'],
});

export const {
  useGetUsersQuery,
  useGetUserGroupMembershipQuery,
  useGetUserMembershipInheritanceQuery,
  useGetUserTransitiveACLMembershipQuery,
  useGetRoleGroupsQuery,
  useGetACLGroupsQuery,
  useGetRoleGroupACLGroupMembershipQuery,
  useGetUserTransitiveGroupMembershipQuery,
  useGetACLGroupRoleMembersQuery,
  useGetACLGroupDirectUserMembersQuery,
  useGetRolesUserMembersQuery,
  useGetACLWritebackMembershipQuery,
  useGetGroupByIdQuery,
  useGetAADWritebackGroupQuery,
  useGetGroupMembersQuery,
  useGetGroupOwnersQuery,
  useGetGroupDetailsQuery,
  useGetRoleACLGroupMembershipQuery,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useAddGroupOwnerMutation,
  useRemoveGroupOwnerMutation,
  useCreateGroupMutation,
  useAddGroupToGroupMutation,
  //useUpdateGroupMembersMutation,
} = graphApi;
